import { z } from 'zod'

/**
 * env.ts
 * Zod-validated environment schema.
 * : all env access goes through this module.
 *
 * Merge order (later wins):
 *   1. Build-time .env.* values  (import.meta.env)
 *   2. Runtime window.__APP_CONFIG__ (CI/CD token-substituted, used in deployed envs)
 *
 * Unresolved CI/CD token placeholders (e.g. __VITE_API_BASE_URL__) are filtered
 * out so the app gracefully falls back to build-time .env values.
 *
 * Declare new variables in THREE places:
 *   1. envSchema below
 *   2. src/types/env.d.ts  (ImportMetaEnv + Window.__APP_CONFIG__)
 *   3. .env.example  +  public/runtime-config.js
 */

const envSchema = z.object({
  // Azure AD / MSAL — SPA App Registration
  VITE_AZURE_AD_CLIENT_ID: z.string().min(1, 'SPA App Registration Client ID is required'),
  VITE_AZURE_AD_TENANT_ID: z.string().min(1, 'Azure AD Tenant ID is required'),
  VITE_AZURE_AD_AUTHORITY: z.string().url('Authority must be a valid URL'),

  // Azure AD — Backend API App Registration client ID (UUID).
  // Optional when VITE_API_SCOPE is provided explicitly (Blazor user_impersonation pattern).
  VITE_MSAL_CLIENT_ID: z.string().optional(),

  // API
  VITE_API_BASE_URL:    z.string().url('API base URL must be a valid URL'),
  VITE_SIGNALR_HUB_URL: z.string().url('SignalR hub URL must be a valid URL'),
  // API OAuth scope — maps to Blazor AzureAdTokenConfig.Scopes
  VITE_API_SCOPE:       z.string().optional(),
  // Dev-only API key — used instead of MSAL bearer token when VITE_DISABLE_AUTH=true
  VITE_API_KEY:         z.string().optional(),

  // Profiles API — for header profile photo (matches Blazor ApplicationConfigs.ProfilesAPI)
  // Full URL template with {email} placeholder — apikey is embedded in the URL.
  // Example: https://sdu.deloitte.com/api/profiles/v1/DeloitteProfiles/profilepicture?size=standard&apikey=xxx&search={email}
  VITE_PROFILES_API_URL: z.string().optional(),
  VITE_ACCESS_REQUEST_OUTLOOK_URL: z.string().url('Access request Outlook URL must be a valid URL').default('https://outlook.office.com/mail/deeplink/compose'),

  // App
  VITE_APP_TITLE:        z.string().min(1).default('DU Digital Badging'),
  VITE_APP_INSIGHTS_KEY: z.string().default(''),
  VITE_ENABLE_MOCK_API:  z.enum(['true', 'false']).default('false'),
})

export type Env = z.infer<typeof envSchema>
type RuntimeConfig = Partial<Record<keyof z.input<typeof envSchema>, string>>

/** Detects unresolved CI/CD token placeholders like __VITE_API_BASE_URL__ */
function isUnresolvedToken(value: string): boolean {
  return /^__[^\s]+__$/.test(value)
}

function getRuntimeConfig(): RuntimeConfig {
  if (typeof window === 'undefined') return {}
  const appConfig = ((window as unknown) as Record<string, unknown>).__APP_CONFIG__
  if (!appConfig || typeof appConfig !== 'object') return {}
  const filteredEntries = Object.entries(appConfig as Record<string, unknown>).filter(
    ([, value]) => typeof value === 'string' && !isUnresolvedToken(value),
  )
  return Object.fromEntries(filteredEntries) as RuntimeConfig
}

const parsed = envSchema.safeParse({
  ...import.meta.env,     // build-time .env values
  ...getRuntimeConfig(),  // runtime window.__APP_CONFIG__ (overrides; used in deployed envs)
})

if (!parsed.success) {
  console.error('[env] Invalid environment configuration:', parsed.error.format())
  throw new Error('[env] Missing or invalid environment variables. Check the console for details.')
}

export const env: Env = parsed.data

